"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import { GoogleContactForm, type GoogleContactFormProps } from "@modes0/ui-components/client";
import { createServiceContactFormContent } from "@/content/business-details/business-content";

const GoogleReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
    ssr: false,
});

type ServiceContactFormContent = ReturnType<typeof createServiceContactFormContent>;

type ServiceContactFormProps = {
    content: ServiceContactFormContent;
    className?: string;
};

type WindowWithGoogleRecaptcha = Window & {
    grecaptcha?: {
        getResponse?: () => string;
        reset?: () => void;
    };
};

function getGoogleRecaptchaResponse() {
    if (typeof window === "undefined") return null;

    const token = (window as WindowWithGoogleRecaptcha).grecaptcha?.getResponse?.();
    return token || null;
}

export default function ServiceContactForm({ className, content }: ServiceContactFormProps) {
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaKey, setCaptchaKey] = useState(0);
    const [formResetNonce, setFormResetNonce] = useState(0);
    const captchaSiteKey = content.captcha.siteKey;
    const formConfig = content.form as GoogleContactFormProps;

    const resetCaptcha = () => {
        setCaptchaToken(null);
        (window as WindowWithGoogleRecaptcha).grecaptcha?.reset?.();
        setCaptchaKey((key) => key + 1);
    };

    const getCaptchaToken = () => captchaToken || getGoogleRecaptchaResponse();

    const handleSuccess: GoogleContactFormProps["onSuccess"] = (response, payload) => {
        formConfig.onSuccess?.(response, payload);
        setFormResetNonce((nonce) => nonce + 1);
    };

    return (
        <GoogleContactForm
            {...formConfig}
            className={className || "grow-leads-contact-form grow-leads-service-contact-form"}
            initialValues={{
                ...(formConfig.initialValues ?? {}),
                __resetNonce: formResetNonce,
            }}
            onSuccess={handleSuccess}
            captchaElement={
                captchaSiteKey ? (
                    <div className="grow-leads-recaptcha-field grow-leads-service-recaptcha-field">
                        <div className="grow-leads-recaptcha-copy">
                            <span className="grow-leads-recaptcha-label">Security check</span>
                            <span className="grow-leads-recaptcha-note">
                                Tick the box before sending your enquiry.
                            </span>
                        </div>
                        <GoogleReCAPTCHA
                            key={captchaKey}
                            sitekey={captchaSiteKey}
                            onChange={(token) => setCaptchaToken(token)}
                            onExpired={() => setCaptchaToken(null)}
                            onErrored={() => setCaptchaToken(null)}
                        />
                    </div>
                ) : undefined
            }
            getCaptchaToken={captchaSiteKey ? getCaptchaToken : undefined}
            resetCaptcha={captchaSiteKey ? resetCaptcha : undefined}
        />
    );
}
